<!--
Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->
<div
    fxFlex
    fxLayout="row"
    class="main-dialog-sheet"
    [class.has-image-layer]="data?.threeQuarterSheetData?.backgroundImageUrl"
    nvSpatialNavigationContainer
    nvTrapFocus
>
    @if (data?.threeQuarterSheetData?.backgroundImageUrl) {
        <div
            class="image-layer"
            nvHigBackgroundImageOverlay
            [imageUrl]="data?.threeQuarterSheetData?.backgroundImageUrl"
            role="presentation"
            aria-hidden="true"
        ></div>
    }
    <div
        class="content-container"
        fxLayout="column"
        fxLayoutAlign="space-between start"
        [class.translucent]="
            data?.threeQuarterSheetData?.backgroundImageUrl && !data?.threeQuarterSheetData?.disableTranslucent
        "
        [class.has-background-image]="
            data?.threeQuarterSheetData?.backgroundImageUrl && data?.threeQuarterSheetData?.disableTranslucent
        "
    >
        @if (data?.threeQuarterSheetData?.title) {
            <div class="title font-sub1">
                <span class="title-content" [nvHeadingFocusable]="data?.threeQuarterSheetData?.titleHeadingLevel || 2">
                    {{
                        data.threeQuarterSheetData.title.translated
                            ? data.threeQuarterSheetData.title.text
                            : (data.threeQuarterSheetData.title.text | translate)
                    }}
                </span>
                @if (
                    data?.threeQuarterSheetData?.subtitle?.parts?.length ||
                    data?.threeQuarterSheetData?.subtitle?.status
                ) {
                    <span class="font-body2 font-color-tertiary subtitle-content">
                        @for (part of data.threeQuarterSheetData.subtitle.parts; track part) {
                            @if (part.type === 'icon') {
                                <img class="subtitle-icon" [src]="part.imageUrl" alt="" aria-hidden="true" />
                            }
                            @if (part.type === 'text') {
                                <span [attr.aria-hidden]="part.ariaHidden || null">{{
                                    part.translated ? part.text : (part.text | translate)
                                }}</span>
                            }
                        }
                        @if (data?.threeQuarterSheetData?.subtitle?.status) {
                            <span class="subtitle-status">{{
                                data.threeQuarterSheetData.subtitle.status | translate
                            }}</span>
                        }
                        @if (data?.threeQuarterSheetData?.subtitle?.showCheckIcon) {
                            <mat-icon class="subtitle-check-icon" svgIcon="check" aria-hidden="true"></mat-icon>
                        }
                    </span>
                }
            </div>
        }
        @if (data.headerText) {
            <span class="content-header font-header4" [nvHeadingFocusable]="data.headerHeadingLevel">
                {{
                    data.headerText.translated
                        ? data.headerText.text
                        : (data.headerText.text | translate: data.headerText.placeholders)
                }}
            </span>
        }
        <div class="content-scrollable" fxLayout="column" fxLayoutGap="32px">
            <!-- Warning/error banner using nv-hig-list-insert -->
            @if (data.listInsertData) {
                <div>
                    <nv-hig-list-insert [data]="data.listInsertData"></nv-hig-list-insert>
                </div>
            }
            <!-- Top body text - renders above QR code section when present -->
            @if (data.topBodyText) {
                <div
                    class="font-body2"
                    [innerHTML]="
                        data.topBodyText.translated
                            ? data.topBodyText.text
                            : (data.topBodyText.text | translate: data.topBodyText.placeholders)
                    "
                ></div>
            }
            @if (data.showSpinner || data.image?.url || data.bodyText?.text || data.descriptionList) {
                <div class="content-info" fxLayout="row" [class.has-qrcode]="data.image?.url">
                    @if (data.showSpinner) {
                        <mat-progress-spinner
                            mode="indeterminate"
                            diameter="72"
                            color="accent"
                            role="status"
                            [attr.aria-label]="'loading' | translate"
                        ></mat-progress-spinner>
                    }
                    @if (data.image?.url) {
                        <div class="qrcode-container">
                            <qrcode
                                role="img"
                                [attr.aria-label]="'common.qrCode' | translate"
                                [qrdata]="data.image.url"
                                [width]="200"
                                [errorCorrectionLevel]="'M'"
                            ></qrcode>
                        </div>
                    }
                    <div fxFlex fxLayout="column" fxLayoutGap="16px">
                        <!-- Row layout only applies with an icon, so icon-less sheets keep the plain block flow. -->
                        @if (data.bodyText?.text) {
                            <div [class.body-text-row]="data.bodyIcon">
                                @if (data.bodyIcon) {
                                    <mat-icon
                                        class="body-text-icon"
                                        [ngClass]="data.bodyIconColorTheme"
                                        [svgIcon]="data.bodyIcon"
                                        aria-hidden="true"
                                    ></mat-icon>
                                }
                                <div
                                    class="font-body2"
                                    [innerHTML]="
                                        data.bodyText.translated
                                            ? data.bodyText.text
                                            : (data.bodyText.text | translate: data.bodyText.placeholders)
                                    "
                                ></div>
                            </div>
                        }
                        @if (data.descriptionList) {
                            <div
                                fxLayout="column"
                                fxLayoutGap="16px"
                                class="hig-description-list-container"
                                role="list"
                            >
                                @for (option of data.descriptionList; track option) {
                                    <div class="hig-description-list-item" role="listitem">
                                        @if (option.matIcon) {
                                            <mat-icon
                                                class="hig-description-icon font-body2"
                                                [svgIcon]="option.matIcon"
                                                aria-hidden="true"
                                            ></mat-icon>
                                        }
                                        <div class="hig-description-text font-body2">{{ option.text }}</div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </div>
            }
            <!-- Account cards (structured nv-hig-list-item rendering) -->
            @if (data.higCardData?.length) {
                @for (card of data.higCardData; track card) {
                    <div class="hig-account-card-grid">
                        <nv-hig-list-item [cardData]="card"></nv-hig-list-item>
                    </div>
                }
            }
            <!-- generic upgrades -->
            @if (data.dataCells?.length > 0) {
                <nv-hig-cell-comparison [data]="data.dataCells"> </nv-hig-cell-comparison>
            }
            <!-- Custom body slot: Example used in IAP -->
            <ng-content select="[nvHigSheetBody]"></ng-content>
        </div>

        @if (data.footerText) {
            <div fxLayout="row" fxLayoutAlign="end end">
                <div class="font-body3">
                    {{
                        data.footerText.translated
                            ? data.footerText.text
                            : (data.footerText.text | translate: data.footerText.placeholders)
                    }}
                </div>
            </div>
        }
    </div>

    @if (data?.threeQuarterSheetData?.backgroundImageUrl) {
        <div
            class="background-scrim"
            [class.translucent]="!data?.threeQuarterSheetData?.disableTranslucent"
            role="presentation"
            aria-hidden="true"
        >
            <div class="vertical-divider" role="presentation"></div>
        </div>
    }

    <div
        fxLayout="column"
        class="button-container"
        [class.translucent]="
            data?.threeQuarterSheetData?.backgroundImageUrl && !data?.threeQuarterSheetData?.disableTranslucent
        "
        [class.has-background-image]="
            data?.threeQuarterSheetData?.backgroundImageUrl && data?.threeQuarterSheetData?.disableTranslucent
        "
        [class.has-section-headers]="data?.radioButtonOptions?.[0]?.sectionHeader"
    >
        <!-- Spinner in button area (for sync in progress states) -->
        @if (data?.showSpinnerInButtonArea) {
            <mat-progress-spinner
                class="button-area-spinner"
                mode="indeterminate"
                color="accent"
                [diameter]="72"
                aria-hidden="true"
            ></mat-progress-spinner>
        }

        <!-- radio buttons with optional section headers -->
        @for (option of data.radioButtonOptions; track option; let i = $index; let isFirst = $first) {
            <!-- Section header with divider (only rendered if sectionHeader is provided) -->
            @if (option?.sectionHeader) {
                <div class="radio-section-header font-sub1" [nvHeadingFocusable]="3">
                    {{ option.sectionHeader | translate }}
                </div>
                <!-- Divider line after section header -->
                <mat-divider aria-hidden="true" class="section-divider" role="separator"></mat-divider>
            }
            @if (option) {
                <button
                    mat-button
                    class="radio-button-option"
                    [class.has-status]="option.statusText"
                    (click)="onRadioButtonClick(option.value)"
                    [nvAutoFocus]="isFirst && autoFocusTarget === 'radio'"
                    [attr.aria-labelledby]="
                        option.statusText ? 'radio-store-' + i + ' radio-status-' + i : 'radio-store-' + i
                    "
                >
                    @if (option.imageUrl) {
                        <img class="prefix-image" [src]="option.imageUrl" alt="" aria-hidden="true" />
                    }
                    <!-- aria-hidden on label/status spans prevents screen readers from reading them as
          standalone text inside the button; aria-labelledby on the button still resolves
          their text content, so the accessible name is announced exactly once. -->
                    <div class="radio-option-content">
                        <span [id]="'radio-store-' + i" aria-hidden="true" class="radio-option-label font-sub2"
                            >{{ option.translated ? option.value : (option.value | translate) }}
                            @if (option.statusText) {
                                <span class="cdk-visually-hidden">,</span>
                            }
                        </span>
                        @if (option.showCheckmark) {
                            <mat-icon class="radio-option-checkmark" svgIcon="check" aria-hidden="true"></mat-icon>
                        }
                        @if (option.statusText) {
                            <span
                                [id]="'radio-status-' + i"
                                aria-hidden="true"
                                class="font-body3 font-color-tertiary radio-option-status"
                                >{{ option.statusText | translate }}</span
                            >
                        }
                    </div>
                </button>
            }
        }

        <!-- action buttons -->
        @if (data.primaryButton && !data.radioButtonOptions) {
            <button
                mat-button
                (click)="onPrimaryButtonClick()"
                class="sentence-case"
                [class.spinner-loading]="data.primaryButton.spinnerOverlay"
                [disabled]="data.primaryButton.isDisabled"
                [nvAutoFocus]="autoFocusTarget === 'primary'"
                [attr.aria-label]="data.primaryButton.text | translate"
            >
                <span class="primary-button-label">{{ data.primaryButton.text | translate }}</span>
                @if (data.primaryButton.svgIcon) {
                    <mat-icon
                        class="link-icon"
                        [svgIcon]="data.primaryButton.svgIcon"
                        aria-hidden="true"
                        iconPositionEnd
                    ></mat-icon>
                }
                @if (data.primaryButton.spinnerOverlay) {
                    <span class="spinner-overlay" aria-hidden="true">
                        <mat-progress-spinner
                            class="mat-accent"
                            mode="indeterminate"
                            [diameter]="36"
                            aria-hidden="true"
                        ></mat-progress-spinner>
                    </span>
                }
            </button>
        }
        @if (data.checkbox?.primaryActionInThreeQuarterSheet) {
            <ng-container [ngTemplateOutlet]="checkboxButton"></ng-container>
        }

        @if (data.checkbox && !data.checkbox.primaryActionInThreeQuarterSheet) {
            <ng-container [ngTemplateOutlet]="secondaryButton"></ng-container>
            <ng-container [ngTemplateOutlet]="checkboxButton"></ng-container>
        } @else {
            @if (data.secondaryButton) {
                <button
                    mat-button
                    class="sentence-case"
                    (click)="onSecondaryButtonClick()"
                    [attr.aria-label]="data.secondaryButton.text | translate"
                >
                    {{ data.secondaryButton.text | translate }}
                </button>
            }
        }

        @for (button of data.threeQuarterSheetData?.augmentedButtons; track button; let isFirst = $first) {
            <button
                class="block-align"
                mat-button
                (click)="onAugmentedButtonClick(button.id)"
                [disabled]="button.isDisabled"
                [nvAutoFocus]="isFirst && autoFocusTarget === 'augmented'"
                [attr.aria-label]="
                    (button.text | translate) +
                    (button?.hasNotificationBadge ? ' - ' + ('threeQuarterSheet.hasNotification' | translate) : '')
                "
            >
                @if (button?.hasNotificationBadge) {
                    <span class="notification-badge" aria-hidden="true"></span>
                }
                <span>{{ button.text | translate }}</span>
                @if (button?.svgIcon) {
                    <mat-icon
                        class="link-icon"
                        [svgIcon]="button.svgIcon"
                        aria-hidden="true"
                        iconPositionEnd
                    ></mat-icon>
                }
            </button>
        }

        <!-- link -->
        @if (shouldShowLinkRegion) {
            <div fxLayout="column" fxLayoutGap="16px">
                <mat-divider
                    class="separator"
                    role="separator"
                    [attr.aria-label]="'threeQuarterSheet.sectionDivider' | translate"
                ></mat-divider>
                @if (data.link.url || data.link.linkClickedCallback) {
                    <button
                        mat-button
                        class="block-align"
                        (click)="
                            onLinkClick(
                                data.link.url,
                                data.link.shortUrl,
                                data.link.shortUrlDescription,
                                data.link.linkClickedCallback
                            )
                        "
                        [attr.aria-label]="
                            data.link.translatedAriaLabel || (data.link.text | translate: data.link.placeholders)
                        "
                    >
                        <div>
                            <span>{{ data.link.text | translate: data.link.placeholders }}</span>
                            <mat-icon class="link-icon" svgIcon="open_in_new" aria-hidden="true"></mat-icon>
                        </div>
                    </button>
                }
            </div>
        }

        <!-- templates -->
        <ng-template #secondaryButton>
            @if (data.secondaryButton) {
                <button
                    mat-button
                    class="sentence-case"
                    [disabled]="data.secondaryButton.isDisabled"
                    (click)="onSecondaryButtonClick()"
                    [attr.aria-label]="data.secondaryButton.text | translate"
                >
                    {{ data.secondaryButton.text | translate }}
                </button>
            }
        </ng-template>

        <ng-template #checkboxButton>
            @if (data.checkbox) {
                <button
                    mat-button
                    class="sentence-case"
                    (click)="onCheckboxClick()"
                    [attr.aria-label]="data.checkbox.text | translate"
                    role="checkbox"
                    [attr.aria-checked]="data.checkboxState ? 'true' : 'false'"
                >
                    {{ data.checkbox.text | translate }}
                </button>
            }
        </ng-template>
    </div>
</div>
